<template>
    <div
        class="quill-editor-wrapper"
        :class="{ 'print-box-overflow': isPrintBoxOverflowing }"
    >
        <quill-editor
            v-model="internalContent"
            ref="quillEditorRef"
            :options="editorOption"
            @blur="onEditorBlur"
            @focus="onEditorFocus"
            @ready="onEditorReady"
            @change="onEditorChange"
        />
        <div
            v-if="printBox"
            class="print-box-status"
            data-cy="print-box-status"
        >
            <span
                class="print-box-status__label"
                :class="printFit.evaluable && !printFit.fits ? 'tw:text-red-600' : 'tw:text-gray-600'"
                data-cy="print-box-status-label"
            >{{ printFitLabel }}</span>
            <span
                v-if="printFit.evaluable"
                class="print-box-status__measure"
                data-cy="print-box-measure"
            >{{ printFitMeasure }}</span>
        </div>
        <div
            v-if="printBox"
            ref="printBoxProbe"
            class="print-box-probe"
            aria-hidden="true"
            :style="probeStyle"
            v-html="measuredHtml"
        ></div>
    </div>
</template>

<script setup>
    import { computed, ref, toRef } from 'vue';
    import { quillEditor } from 'vue-quill-editor';
    import 'quill/dist/quill.core.css';
    import 'quill/dist/quill.snow.css';
    import 'quill/dist/quill.bubble.css';
    import { useQuillEditor } from '@/composables/common/useQuillEditor';
    import { useQuillInlineStyles } from '@/composables/common/useQuillInlineStyles';
    import { buildProbeStyle, usePrintBoxFit } from '@/composables/common/usePrintBoxFit';

    const props = defineProps({
        value: {
            type: String,
            default: ''
        },
        options: {
            type: Object,
            default: () => ({})
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: ''
        },
        imageUploadUrl: {
            type: String,
            default: ''
        },
        theme: {
            type: String,
            default: 'snow'
        },
        useInlineStyles: {
            type: Boolean,
            default: true
        },
        /**
         * Caja de la plantilla de impresión donde irá este contenido.
         * Sin esta prop el editor no aplica ningún límite: es opcional para no
         * cambiar el comportamiento de los demás usos del componente.
         *
         * Forma: `PrintBoxGeometry` (en
         * `@/composables/common/usePrintBoxFit`) → `{ width, maxHeight,
         * baseFontSize, lineHeight, paragraphSpacingPx, tolerancePx? }`.
         * El hueco entre párrafos es `paragraphSpacingPx`, en **px fijos**: el
         * motor no lee `paragraphSpacingEm`, así que pasar ese nombre dejaría la
         * medición por debajo del alto real.
         *
         * @type {import('../../composables/common/usePrintBoxFit').PrintBoxGeometry|null}
         */
        printBox: {
            type: Object,
            default: null
        }
    });

    const emit = defineEmits(['input', 'change', 'blur', 'focus', 'ready']);

    const {
        quillEditorRef,
        internalContent,
        editorOption,
        onEditorBlur,
        onEditorFocus,
        onEditorReady,
        onEditorChange,
        processAndGetHTML,
        getQuill,
        getHTML,
        getText,
        getLength,
        insertText,
        deleteText,
        formatText,
    } = useQuillEditor(props, emit);

    const { convertQuillToInline } = useQuillInlineStyles();

    const printBoxProbe = ref(null);

    /**
     * Se mide el HTML convertido a estilos inline porque es exactamente lo que
     * persiste `processAndGetHTML()` y por tanto lo que renderiza el PDF. Medir el
     * HTML con clases de Quill daría otro resultado: `class="ql-size-small"` no
     * aplica sin el CSS de Quill cargado, mientras que `font-size: 0.75em` sí.
     */
    const pdfHtml = computed(() =>
        props.printBox ? convertQuillToInline(internalContent.value) : ''
    );

    /**
     * La sonda renderiza el mismo HTML que ya se guarda y que `Show.vue` pinta con
     * `v-html`. No cruza ningun limite de confianza nuevo: el contenido de
     * `condiciones` es siempre de origen backoffice (el formulario del front no
     * envia ese campo).
     */
    const { measuredHtml, printFit, isEnabled, flush: flushMeasure } = usePrintBoxFit({
        html: pdfHtml,
        printBox: toRef(props, 'printBox'),
        probeRef: printBoxProbe
    });

    const probeStyle = computed(() => (props.printBox ? buildProbeStyle(props.printBox) : null));

    const isPrintBoxOverflowing = computed(() =>
        isEnabled.value && printFit.value.evaluable && !printFit.value.fits
    );

    const printFitLabel = computed(() => {
        if (!printFit.value.evaluable) {
            return 'No se pudo medir el contenido';
        }

        return printFit.value.fits
            ? 'Entra en la plantilla de impresión'
            : 'No entra en la plantilla: se sale al imprimir';
    });

    const printFitMeasure = computed(() =>
        printFit.value.evaluable
            ? `${Math.round(printFit.value.measuredHeight)} / ${printFit.value.maxHeight} px`
            : ''
    );

    /**
     * Ajuste del contenido ACTUAL, forzando la medicion pendiente.
     *
     * `printFit` se recalcula con debounce (120 ms), asi que leerlo directo al
     * guardar devolveria la medicion ANTERIOR si el usuario escribe y pulsa guardar
     * dentro de esa ventana. Esta operacion vacia la medicion en espera, mide el
     * contenido de ahora y la devuelve: el padre DEBE esperarla antes de decidir.
     *
     * @returns {Promise<Object>} ajuste del contenido actual
     */
    const flushPrintFit = async() => {
        if (!isEnabled.value) {
            return printFit.value;
        }

        return flushMeasure();
    };

    defineExpose({
        processAndGetHTML,
        getQuill,
        getHTML,
        getText,
        getLength,
        insertText,
        deleteText,
        formatText,
        flushPrintFit,
    });
</script>

<style scoped>
    ::v-deep .ql-toolbar {
        border-radius: 4px 4px 0 0;
    }

    ::v-deep .ql-editor {
        min-height: 200px;
        line-height: 1.5;
    }

    ::v-deep .ql-container {
        border-radius: 0 0 4px 4px;
    }

    .quill-editor-wrapper.print-box-overflow {
        outline: 1px solid #dc3545;
        outline-offset: 2px;
    }

    .print-box-status {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 8px;
        margin-top: 4px;
        font-size: 12px;
    }

    .print-box-status__measure {
        white-space: nowrap;
        color: #6c757d;
    }

    /*
     * Sonda que simula la caja de la plantilla de impresión.
     * `visibility: hidden` (y no `display: none`) es obligatorio: necesita layout
     * para poder medirse. `white-space: pre-wrap` respeta los saltos reales del
     * contenido, y los blancos del HTML los quita el composable antes de medir.
     */
    .print-box-probe {
        position: absolute;
        top: 0;
        left: -100000px;
        visibility: hidden;
        pointer-events: none;
        white-space: pre-wrap;
        word-wrap: break-word;
    }

    /*
     * `::v-deep` es obligatorio: los <p> llegan por `v-html` y NO llevan el
     * atributo de scope, asi que sin el deep la regla compilaria a
     * `.print-box-probe p[data-v-x]` y no aplicaria nunca.
     *
     * Neutraliza el reboot de Bootstrap (`p { margin-bottom: 1rem }`).
     */
    .print-box-probe ::v-deep p {
        margin: 0;
        padding: 0;
    }

    /*
     * Solo cuenta el hueco ENTRE parrafos, no el del ultimo. La sonda es
     * `position: absolute`, lo que crea un Block Formatting Context: ahi los
     * margenes no colapsan, asi que el margen del ultimo parrafo se sumaria al
     * alto y daria 220,38 px en vez de 210,78 px. La plantilla tampoco cuenta ese
     * hueco final: su texto de referencia termina justo en el borde de la caja.
     */
    .print-box-probe ::v-deep p:not(:last-child) {
        margin-bottom: var(--print-box-paragraph-gap);
    }
</style>
